import React from "react";
import { ColorType } from "@ifgengineering/component-library";
import { ThemedText } from "@components/ThemedText";
import useCurrency from "@helpers/useCurrency";
import { Currency } from "../../portfolio/v2/types";
import { SummaryCardContainer, InfoItem, BottomInfoContainer } from "./styled";
import ProgressBar from "@components/ProgressBar";
import { TOTAL_YEARLY_CONTRIBUTION } from "../consts";
import { ifIsaAllowanceBreakdown } from "../allowance";

interface IfIsaStatusProps {
  currency?: Currency;
  onClick?: () => void;
  amountInvested: number;
  availableToInvest?: number;
  pendingMovesIn?: number;
  /** The API's confirmed use; the card falls back to working it out when absent */
  allowanceUsed?: number;
  barConfig?: {
    height?: number;
    bgColor?: ColorType;
    color?: ColorType;
    exceedColor?: ColorType;
  };
}

const IfIsaStatus: React.FC<IfIsaStatusProps> = ({
  currency = "GBP",
  amountInvested,
  availableToInvest,
  pendingMovesIn,
  allowanceUsed,
  onClick,
  barConfig,
}) => {
  barConfig = {
    height: 24,
    bgColor: "SLATE300",
    color: "GREEN600",
    exceedColor: "ERROR300",
    ...barConfig,
  };

  const { toCurrency } = useCurrency(currency, {
    noDecimals: true,
  });

  const {
    used: usedAllowance,
    pending,
    remaining: remainingAmount,
    usedPercentage: progressPercentage,
  } = ifIsaAllowanceBreakdown({
    availableToInvest,
    totalContribution: amountInvested,
    pendingMovesIn,
    allowanceUsed,
  });

  const isExceeding =
    typeof availableToInvest === "number"
      ? availableToInvest < 0
      : amountInvested > TOTAL_YEARLY_CONTRIBUTION;
  const barColor = isExceeding ? barConfig.exceedColor : barConfig.color;

  return (
    <SummaryCardContainer onClick={onClick}>
      <Info label="Yearly ISA Limit" value="£20,000" />
      <ProgressBar
        percentage={progressPercentage}
        barColor={barColor}
        barBgColor={barConfig.bgColor}
        barHeight={barConfig.height}
      />
      <BottomInfoContainer>
        <Info label="Allowance Used" value={toCurrency(usedAllowance)} />
        <Info label="Remaining" value={toCurrency(remainingAmount)} />
      </BottomInfoContainer>
      {pending > 0 && (
        <ThemedText
          type="P14"
          color="SLATE600"
          testId="ifisa-summary-pending-moves"
        >
          {toCurrency(pending)} held by pending moves into your IF-ISA
        </ThemedText>
      )}
    </SummaryCardContainer>
  );
};

export default IfIsaStatus;

const Info = ({ label, value }: { label: string; value: string }) => (
  <InfoItem>
    <ThemedText type="S14" color="SLATE700">
      {label}
    </ThemedText>
    <ThemedText type="S16" color="SLATE900">
      {value}
    </ThemedText>
  </InfoItem>
);
